<app-dfp-panel title="Pontos de destaque" [titleNote]="scaleTitleNote">
  <ng-container panelActions>
    <app-period-column-add
      [availablePeriods]="availablePeriods"
      [hiddenYears]="hiddenYears"
      (periodAdd)="periodAdd.emit($event)"
      (yearRestore)="yearRestore.emit($event)" />
    @if (allowExport) {
      <app-button
        icon="pi pi-download"
        [pureIcon]="true"
        [text]="true"
        title="Exportar"
        (sendClick)="exportMenu.toggle($event)" />
      <p-menu #exportMenu [popup]="true" [model]="exportMenuItems" [appendTo]="'body'" />
    }
  </ng-container>

  <!-- Topo do painel, entre a barra de título e a tabela: a leitura do Cog (card 3801), projetada pela DFP. -->
  <ng-content select="[panelLead]" />

  <!-- Mesma rolagem da DRE e do Balanço: barra também no topo, abrindo no ano mais recente, e nada de número pela
  metade na borda (card 3801). -->
  <div
    class="performance-table performance-table--highlights"
    appTopScrollbar
    appDfpClipGuard
    [scrollToEndOnChange]="companyId">
    <p-table [value]="groupedHistoricalHighlights" [tableStyle]="{ 'min-width': '100%' }">
      <ng-template pTemplate="header">
        <tr>
          <th appDfpColumnWidth="highlights:name">Indicador</th>
          @for (column of columns; track column.key; let last = $last; let i = $index) {
            <th
              [appDfpColumnWidth]="'highlights:' + column.key"
              [class.is-latest]="last"
              [class.is-zebra]="i % 2 === 1">
              @if (!column.sublabel && availablePeriods !== null) {
                <app-year-column-menu
                  [label]="column.label"
                  [year]="column.year"
                  [availablePeriods]="availablePeriods"
                  [canModify]="visibleYearColumnsCount > 1"
                  [hiddenYears]="hiddenYears"
                  [sources]="sourcesForColumn(column)"
                  [companyId]="companyId"
                  (replace)="yearReplace.emit({ year: column.year, column: $event })"
                  (remove)="yearRemove.emit(column.year)"
                  (restore)="yearRestore.emit($event)" />
              } @else {
                <div>{{ column.label }}</div>
              }
              @if (column.sublabel) {
                <div class="column-period">
                  {{ column.sublabel }}
                  @if (availablePeriods !== null) {
                    <button
                      type="button"
                      class="remove-period-button ignore-export"
                      title="Remover esta coluna"
                      (click)="periodRemove.emit(column.key)">
                      ×
                    </button>
                  }
                </div>
              }
            </th>
          }
        </tr>
      </ng-template>

      <ng-template pTemplate="body" let-group>
        <tr [class.is-group]="group.hasSubItems" [attr.data-level]="exportLevel.group">
          <!-- Dívida Líquida (card 3669): "+" à esquerda do nome abre a composição; começa fechada. -->
          <td
            class="nowrap"
            [class.expander]="compositionFor(group).length"
            (click)="toggleComposition(group)">
            @if (compositionFor(group).length) {
              <span
                class="dfp-toggle ignore-export"
                role="button"
                [attr.aria-expanded]="isCompositionOpen(group)"
                [attr.aria-label]="
                  (isCompositionOpen(group) ? 'Fechar' : 'Abrir') + ' composição de ' + group.name
                "
                [class.is-expanded]="isCompositionOpen(group)"></span>
            }
            {{ group.name }}
            <!-- Memória de cálculo do destaque (card 3593). O clique no ícone não abre a composição. -->
            <app-dfp-calculation-info
              [name]="group.name"
              [columns]="calculationColumnsForHighlight(group.name)"
              [formatPart]="formatHighlightPartFor(group.name)"
              (click)="$event.stopPropagation()" />
          </td>
          @for (column of columns; track column.key; let last = $last; let i = $index) {
            <td [class.is-zebra]="i % 2 === 1" [class.is-latest]="last">
              <!-- Holding pura (card 3669): "Não se aplica" no lugar do número, motivo no tooltip. -->
              @if (notApplicableReasonIn(group.historic, column); as reason) {
                <app-not-applicable-value [reason]="reason" />
              } @else {
                {{ formatHighlightValue(group.name, getValueByColumn(group.historic, column)) }}
              }
              @if (isAnnualizedInColumn(group.historic, column)) {
                <span
                  class="annualized-badge"
                  title="Valor anualizado (12 ÷ meses do período) para ser comparável a um ano cheio">
                  anualizado
                </span>
              }
            </td>
          }
        </tr>

        <!--
          Composição da Dívida Líquida (card 3669): parcelas prontas do backend, com o sinal da fórmula (caixa e
          aplicações negativos). Fechada, a linha fica no DOM com display none: a exportação leva as parcelas.
        -->
        @for (part of compositionFor(group); track part.key) {
          <tr
            class="is-composition"
            [style.display]="isCompositionOpen(group) ? null : 'none'"
            [attr.data-level]="exportLevel.composition">
            <td class="nowrap">{{ part.label }}</td>
            @for (column of columns; track column.key; let last = $last; let i = $index) {
              <td [class.is-zebra]="i % 2 === 1" [class.is-latest]="last">
                {{ compositionValue(part, column) }}
              </td>
            }
          </tr>
        }

        @for (sale of group.historic; track sale.name) {
          @if (sale.name !== group.name) {
            <tr [attr.data-level]="exportLevel.account">
              <td class="nowrap">
                {{ sale.name }}
                <app-dfp-calculation-info
                  [name]="sale.name"
                  [columns]="calculationColumnsForHighlight(sale.name)"
                  [formatPart]="formatHighlightPartFor(sale.name)" />
              </td>
              @for (column of columns; track column.key; let last = $last; let i = $index) {
                <td [class.is-zebra]="i % 2 === 1" [class.is-latest]="last">
                  @if (notApplicableReasonIn(sale.years, column); as reason) {
                    <app-not-applicable-value [reason]="reason" />
                  } @else {
                    {{ formatHighlightValue(sale.name, getValueByColumn(sale.years, column)) }}
                  }
                  @if (isAnnualizedInColumn(sale.years, column)) {
                    <span
                      class="annualized-badge"
                      title="Valor anualizado (12 ÷ meses do período) para ser comparável a um ano cheio">
                      anualizado
                    </span>
                  }
                </td>
              }
            </tr>
          }
        }
      </ng-template>
    </p-table>
  </div>
</app-dfp-panel>
